import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import { Table, Badge, Button, Spinner } from "@/components/ui";
import { EligibilityRadio } from "../EligibilityRadio";
import { EligibilityCriterion, EligibilityLevel } from "@/services/bedrock";
import { Plus } from "lucide-react";

interface OwnProps {
  level: EligibilityLevel;
  currentRule: EligibilityCriterion | undefined;
  tags: string[];
  onIncludeFlagChanged: (flag: boolean) => void;
  onUpdateClicked: () => void;
  loading?: boolean;
  readOnly?: boolean;
}

type Props = OwnProps;

const EligibilityRuleFC: React.FC<Props> = ({
  level,
  currentRule,
  tags,
  onUpdateClicked,
  onIncludeFlagChanged,
  loading,
  readOnly,
}) => {
  const [included, setIncluded] = useState(!!currentRule ? currentRule.isInclude : true);

  useEffect(() => {
    setIncluded(!!currentRule ? currentRule.isInclude : true);
  }, [currentRule?.isInclude]);

  const label = useMemo(() => {
    switch (level) {
      case "STATE":
        return "States";
      case "REGION":
        return "Regions";
      case "PLAYER_ID":
        return "Specific Players";
      case "BRANCH":
        return "Branches";
    }
  }, [level]);

  return (
    <Table.Row className="hover:bg-slate-50/40">
      <Table.Cell className="font-semibold text-slate-800 text-sm align-middle py-4">{label}</Table.Cell>
      <Table.Cell className="align-middle py-4">
        <EligibilityRadio
          name={`rule-radio-${level}`}
          included={included}
          onUpdate={f => {
            setIncluded(f);
            onIncludeFlagChanged(f);
          }}
        />
      </Table.Cell>
      <Table.Cell className="align-middle py-4">
        {loading && (
          <div className="flex items-center gap-2 text-xs text-slate-400">
            <Spinner className="size-4 animate-spin text-slate-500" />
            Loading {label.toLowerCase()}...
          </div>
        )}
        {!loading && (
          <div className="flex flex-wrap gap-1.5 items-center max-w-[550px]">
            {tags.length > 0 &&
              tags.map((tag, index) => {
                return (
                  <Badge
                    key={`tag-${index}`}
                    variant="outline"
                    className="bg-slate-100/60 hover:bg-slate-100 border-slate-200 text-slate-800 text-xs px-2.5 py-0.5 rounded-md font-medium shadow-none transition-colors"
                  >
                    {tag}
                  </Badge>
                );
              })}
            {!readOnly && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={onUpdateClicked}
                className={
                  tags.length === 0
                    ? "text-xs font-semibold py-1 px-3 h-8 border-dashed border-slate-300 hover:border-slate-400 text-slate-600 hover:text-slate-700 hover:bg-slate-50/60 gap-1.5 rounded-lg flex items-center transition-colors"
                    : "text-xs font-semibold py-1 px-3 h-8 border-slate-200 hover:border-slate-300 text-slate-600 hover:text-slate-700 hover:bg-slate-50/60 rounded-lg flex items-center transition-colors"
                }
              >
                {tags.length === 0 && <Plus className="size-3.5 stroke-[2.5]" />}
                {tags.length === 0 ? `Add ${label}` : `Edit`}
              </Button>
            )}
          </div>
        )}
      </Table.Cell>
    </Table.Row>
  );
};

export const EligibilityRule = EligibilityRuleFC;
